import { Plus, X, Minus, ShoppingCart, Calculator } from '@phosphor-icons/react';
import { formatCurrency } from '@lib/formatters';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import client from '@api/client';
import { ORDER_API } from '../api/endpoints';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useCartStore from '../stores/cartStore';
import useOrderStore from '../stores/orderStore';
import { storage } from '../utils/storage';
import { getErrorMessage } from '../lib/errors';

const CartDialog = () => {
  const { t } = useTranslation('ordering');
  const navigate = useNavigate();
  const [tableId, setTableId] = useState('');
  const [checkoutError, setCheckoutError] = useState('');
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);

  // --- Zustand store reads ---
  const open = useCartStore((s) => s.cartDialogOpen);
  const cartItems = useCartStore((s) => s.cartItems);
  const setCartItems = useCartStore((s) => s.setCartItems);
  const selectedRestaurantId = useCartStore((s) => s.selectedRestaurantId);
  const setSelectedRestaurantId = useCartStore(
    (s) => s.setSelectedRestaurantId,
  );
  const cartDialogUpdate = useCartStore((s) => s.cartDialogUpdate);
  const setCartDialogUpdate = useCartStore((s) => s.setCartDialogUpdate);
  const newItemtoOrder = useCartStore((s) => s.newItemToOrder);
  const setNewItemtoOrder = useCartStore((s) => s.setNewItemToOrder);
  const erneutbestellen = useCartStore((s) => s.erneutbestellen);
  const setErneutbestellen = useCartStore((s) => s.setErneutbestellen);
  const progress = useCartStore((s) => s.progress);
  const setProgress = useCartStore((s) => s.setProgress);
  const setCartDialogOpen = useCartStore((s) => s.setCartDialogOpen);
  const orderSummary = useOrderStore((s) => s.orderSummary);
  const setOrderSummary = useOrderStore((s) => s.setOrderSummary);
  const setVenueOrderSummary = useOrderStore((s) => s.setVenueOrderSummary);

  const onClose = () => setCartDialogOpen(false);

  const handleQuantityChange = (index, newQty) => {
    const updated = [...cartItems];
    updated[index].quantity = newQty;
    setCartItems(updated);
  };

  const handleRemove = (index) => {
    const updated = [...cartItems];
    updated.splice(index, 1);
    setCartItems(updated);
  };

  const total = Array.isArray(cartItems)
    ? cartItems.reduce(
        (sum, item) => sum + (item.price || 0) * (item.quantity || 0),
        0,
      )
    : 0;

  const handleSetTableId = () => {
    if (!cartItems.length) {
      return;
    }
    if (!cartItems[0].tableId) {
      handleCheckout();
    }
    setTableId(cartItems[0].tableId || '');
  };

  useEffect(() => {
    if (tableId) {
      // Checkout nur aufrufen, wenn newItemToOrder null ist
      if (newItemtoOrder === null) {
        handleCheckout();
      } else {
        handnewOrderItem();
      }
    }
  }, [tableId]);

  const handleCheckout = async () => {
    setCheckoutError('');
    try {
      if (!selectedRestaurantId) {
        return;
      }
      if (!Array.isArray(cartItems) || cartItems.length === 0) {
        return;
      }

      const cleanedCart = cartItems.map((item) => ({
        quantity: Number(item.quantity) || 0,
        menuItemId: item.menuItemId || null,
        specialNotes: item.specialNotes || '',
        components: (item.components || []).flat().map((c) => ({
          value: c?.value || '',
          optionId: c?.optionId || '',
        })),
      }));

      const payload = {
        restaurantId: selectedRestaurantId,
        orderItems: cleanedCart,
      };

      if (tableId && tableId.trim() !== '') {
        payload.tableId = tableId;

        // QR-Flag entscheidet über den Typ
        const qrFlag = storage.getQR();
        if (qrFlag === 'ja') {
          payload.type = 'COUNTER_SERVICE';
          payload.serviceMode = 'TAKEAWAY';
        } else {
          payload.type = 'TABLE_SERVICE';
          payload.serviceMode = 'TAKEAWAY';
        }
      } else {
        payload.type = 'COUNTER_SERVICE';
        payload.serviceMode = 'TAKEAWAY';
      }

      let response;

      if (cartDialogUpdate) {
        const updateOrderId = cartItems[0]?.orderId || orderSummary?.orderId;
        if (!updateOrderId) {
          return;
        }

        const cancelResponse = await client.patch(
          `${ORDER_API}/${updateOrderId}/cancel`,
        );

        if (cancelResponse.status === 200) {
          response = await client.post(ORDER_API, payload);
        } else {
          return; // Abbruch, weil Cancel nicht erfolgreich
        }
      } else {
        response = await client.post(ORDER_API, payload);
      }

      if (response.status === 200 || response.status === 201) {
        const data = response.data;

        const summary = {
          status: data.statusHistory?.[0]?.status || 'Unbekannt',
          statusNotes: data.statusHistory?.[0]?.notes || '',
          orderId: data.id,
          tableId: data.tableId,
          orderNumber: data.orderNumber,
          customerName: data.customerName,
          customerPhone: data.customerPhone,
          specialInstructions: data.specialInstructions,
          subtotal: data.subtotal,
          tax: data.tax,
          total: data.total,
          placedAt: data.placedAt,
          pickupNumber: data.pickupNumber,
          items: data.orderItems.map((item) => ({
            name: item.menuItem.name,
            quantity: item.quantity,
            total: item.totalPrice,
            notes: item.specialNotes,
            components:
              item.components?.map((c) => ({
                value: c.value,
                optionName: c.optionName,
              })) || [],
          })),
        };

        setOrderSummary(summary);
        setCartItems([]);
        setTableId('');

        // nach erfolgreichem Update den Flag zurücksetzen
        if (cartDialogUpdate) {
          setCartDialogUpdate(false);
        }

        onClose();

        const qrValue = storage.getQR() || 'nein';
        if (qrValue === 'ja') {
          storage.setCurrentOrderId(data.id);
        } else if (tableId && tableId.trim() !== '') {
          navigate('/tables');
        }
      }
    } catch (err) {
      setCheckoutError(getErrorMessage(err, t('cart.checkoutError', 'Bestellung fehlgeschlagen. Bitte erneut versuchen.')));
    }
  };

  const handnewOrderItem = async () => {
    setCheckoutError('');
    try {
      if (!selectedRestaurantId) {
        return;
      }
      if (!Array.isArray(cartItems) || cartItems.length === 0) {
        return;
      }

      const cleanedCart = cartItems.map((item, idx) => ({
        quantity: Number(item.quantity) || 0,
        menuItemId: item.menuItemId || null,
        specialNotes: item.specialNotes || '',
        components: (item.components || []).flat().map((c, cIdx) => ({
          value: c?.value || '',
          optionId: c?.optionId || '',
        })),
      }));

      const payload = { orderItems: cleanedCart };

      if (!newItemtoOrder) {
        return;
      }

      const response = await client.post(
        `${ORDER_API}/${newItemtoOrder}/items`,
        payload,
      );

      if (response.status === 200 || response.status === 201) {
        const data = response.data;

        // Warenkorb leeren
        setCartItems([]);
        setTableId('');
        setNewItemtoOrder(null);
        setErneutbestellen(false);
        setProgress(0);

        onClose();

        const qrValue = storage.getQR() || 'nein';

        if (qrValue === 'ja') {
          const currentOrderId = storage.getCurrentOrderId();

          if (!currentOrderId) {
            throw new Error('Keine OrderId im SessionStorage gefunden!');
          }

          const response2 = await client.get(`${ORDER_API}/${currentOrderId}`);

          if (response2.status === 200 || response2.status === 201) {
            const data2 = response2.data;

            const summary = {
              status: data2.statusHistory?.[0]?.status || 'Unbekannt',
              statusNotes: data2.statusHistory?.[0]?.notes || '',
              orderId: data2.id,
              tableId: data2.tableId,
              orderNumber: data2.orderNumber,
              customerName: data2.customerName,
              customerPhone: data2.customerPhone,
              specialInstructions: data2.specialInstructions,
              subtotal: data2.subtotal,
              tax: data2.tax,
              total: data2.total,
              placedAt: data2.placedAt,
              pickupNumber: data2.pickupNumber,
              items:
                data2.orderItems?.map((item) => ({
                  name: item.menuItem?.name,
                  quantity: item.quantity,
                  total: item.totalPrice,
                  notes: item.specialNotes,
                  components:
                    item.components?.map((c) => ({
                      value: c.value,
                      optionName: c.optionName,
                    })) || [],
                })) || [],
            };

            setOrderSummary(summary);
            setCartItems([]);
            setTableId('');

            if (cartDialogUpdate) {
              setCartDialogUpdate(false);
            }

            onClose();

            if (qrValue === 'ja') {
              storage.setCurrentOrderId(data2.id);
            } else if (tableId && tableId.trim() !== '') {
              navigate('/tables');
            }
          }
        } else if (tableId && tableId.trim() !== '') {
          navigate('/tables');
        }
      }
    } catch (err) {
      setCheckoutError(getErrorMessage(err, t('cart.checkoutError', 'Bestellung fehlgeschlagen. Bitte erneut versuchen.')));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      sx={{ zIndex: 1300 }}
    >
      <DialogTitle
        sx={{
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          fontWeight: 'bold',
          fontSize: 22,
          textAlign: 'center',
          py: 2,
        }}
      >
        <ShoppingCart size={24} style={{ marginRight: 8, verticalAlign: 'middle' }} />
        {t('cart.yourCart', 'Ihr Warenkorb')}
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: 'background.default', px: 3 }}>
        {!Array.isArray(cartItems) || cartItems.length === 0 ? (
          <Typography
            variant="body1"
            align="center"
            color="text.secondary"
            sx={{ py: 4 }}
          >
            {t('cart.emptyCart', 'Ihr Warenkorb ist leer.')}
          </Typography>
        ) : (
          <Box display="flex" flexDirection="column" gap={2}>
            {cartItems.map((item, index) => (
              <Paper
                key={item.menuItemId || index}
                elevation={3}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-start"
                >
                  <Box flex={1}>
                    <Typography variant="h6" color="primary.main" fontWeight="bold">
                      {item.menuName}
                    </Typography>
                    {item.restaurantName && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block' }}
                      >
                        {item.restaurantName}
                      </Typography>
                    )}
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      {item.description}
                    </Typography>

                    {Array.isArray(item.components) &&
                      item.components.length > 0 && (
                        <Box mt={2}>
                          <Box component="ul" sx={{ pl: 2, m: 0 }}>
                            {item.components.flat().map((c, i) => (
                              <li key={c.optionId || i} style={{ marginBottom: '4px' }}>
                                <Typography
                                  variant="body2"
                                  fontWeight="bold"
                                  color="text.secondary"
                                  sx={{ mb: 1 }}
                                >
                                  <span
                                    style={{
                                      fontWeight: 'bold',
                                      color: 'inherit',
                                    }}
                                  >
                                    {c.value}:
                                  </span>{' '}
                                  <span style={{ color: 'var(--mui-palette-secondary-main)' }}>
                                    {Array.isArray(c.optionName)
                                      ? c.optionName.join(', ')
                                      : c.optionName}
                                  </span>
                                </Typography>
                              </li>
                            ))}
                          </Box>
                        </Box>
                      )}
                  </Box>

                  <Tooltip title={t('cart.remove', 'Entfernen')}>
                    <IconButton
                      onClick={() => handleRemove(index)}
                      sx={{ color: 'primary.main' }}
                      aria-label="remove"
                    >
                      <X size={24} />
                    </IconButton>
                  </Tooltip>
                </Box>

                <Divider sx={{ my: 1 }} />

                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <IconButton
                      onClick={() =>
                        handleQuantityChange(
                          index,
                          Math.max(1, item.quantity - 1),
                        )
                      }
                      sx={{ color: 'error.main' }}
                      size="small"
                      aria-label="decrease"
                    >
                      <Minus size={24} />
                    </IconButton>
                    <Typography variant="body1" fontWeight="bold">
                      {item.quantity}
                    </Typography>
                    <IconButton
                      onClick={() =>
                        handleQuantityChange(index, item.quantity + 1)
                      }
                      sx={{ color: 'success.main' }}
                      size="small"
                      aria-label="increase"
                    >
                      <Plus size={24} />
                    </IconButton>
                  </Box>

                  <Typography fontWeight="bold" fontSize={16} color="primary.main">
                    {formatCurrency(item.price * item.quantity)}
                  </Typography>
                </Box>
              </Paper>
            ))}
          </Box>
        )}
      </DialogContent>

      <Box sx={{ bgcolor: 'background.default', px: 3, py: 2 }}>
        <Divider sx={{ mb: 1 }} />
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle1" fontWeight="bold">
            {t('cart.subtotal', 'Zwischensumme')}
          </Typography>
          <Typography variant="h6" fontWeight="bold" color="primary.main">
            {formatCurrency(total)}
          </Typography>
        </Box>
      </Box>

      {checkoutError && (
        <Alert severity="error" sx={{ mx: 3, mt: 1 }} onClose={() => setCheckoutError('')}>
          {checkoutError}
        </Alert>
      )}

      <DialogActions sx={{ px: 3, pb: 3 }}>
        {storage.getQR() !== 'ja' && (
          <Button
            onClick={() => setCancelConfirmOpen(true)}
            variant="outlined"
            fullWidth
          >
            {t('cart.cancelOrder', 'Abbrechen')}
          </Button>
        )}

        <Button
          variant="contained"
          fullWidth
          onClick={handleSetTableId}
          color="primary"
          sx={{ fontWeight: 'bold' }}
        >
          {t('cart.placeOrderCharged', 'Kostenpflichtig bestellen')}
        </Button>
      </DialogActions>

      {/* Cancel order confirmation */}
      <Dialog
        open={cancelConfirmOpen}
        onClose={() => setCancelConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('cart.cancelOrder', 'Abbrechen')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('cart.confirmCancel', 'Möchtest du die Bestellung wirklich abbrechen?')}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCancelConfirmOpen(false)}>
            {t('cart.keepOrder', 'Nein, behalten')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              setCancelConfirmOpen(false);
              onClose();
              setCartItems([]);
              navigate('/tables');
            }}
          >
            {t('cart.confirmCancelAction', 'Ja, abbrechen')}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default CartDialog;
